<template>
    <Modal :show="true" class="reservations__schedule__show-slot" @close="close">
        <div class="flex flex-col space-y-4">
            <h3 class="text-lg font-semibold">
                {{ lang('reservations::schedule.modals.show_slot.title') }}:
                {{ props.reservationSlot.name }}
            </h3>
            <div
                class="lg:px-6 lg:py-3 -mx-6 space-y-4 border-gray-300 bg-gray-50 border-y dark:bg-zinc-800 dark:border-zinc-900"
            >
                <div
                    class="relative px-6 py-6 bg-white border rounded-lg dark:bg-neutral-950 dark:border-neutral-700 border-neutral-300"
                >
                    <Button
                        type="button"
                        color="danger"
                        size="xs"
                        class="absolute top-6 right-6"
                        @click="deleteReservation(props.reservationSlot.reservation?.id)"
                    >
                        <span>
                            {{ lang('reservations::reservations.actions.delete') }}
                        </span>
                        <icon icon="hero/outline/x" class="w-4 h-4"></icon>
                    </Button>
                    <div>
                        <span class="inline-block font-medium w-28">
                            {{ lang('reservations::reservations.fields.confirmed_at') }}
                        </span>
                        {{ dateHumanReadable(props.reservationSlot.reservation?.confirmed_at) }}
                    </div>

                    <div>
                        <span class="inline-block font-medium w-28">
                            {{ lang('reservations::reservations.fields.reserved_at') }}
                        </span>
                        {{ dateHumanReadable(props.reservationSlot.reservation?.reserved_at) }}
                    </div>

                    <div class="text-right">
                        <WidgetSlot name="reservation.customer_link" :widgets="props.widgets" />
                    </div>

                    <div v-if="props.metas" class="pt-2 mt-2 border-t border-gray-300">
                        <div v-for="(meta, metaIndex) in metas" :key="meta.key">
                            <h3 class="font-bold">{{ meta.name }}</h3>

                            <div v-if="props.reservationSlot.reservation?.[meta.key]">
                                <div
                                    v-for="value in props.reservationSlot.reservation?.[meta.key]"
                                    :key="value"
                                    class="text-sm"
                                >
                                    <div
                                        v-for="(item, itemKey) in value.data"
                                        :key="itemKey"
                                        class="grid grid-cols-2 gap-4 hover:bg-gray-100 dark:hover:bg-neutral-800 py-0.75"
                                    >
                                        <div class="col-span-1 font-bold">
                                            <!-- TODO: specify language for this field, and fields as objects, elsewhere -->
                                            {{ lang(`reservations::reservations.meta.fields.${itemKey}`) }}
                                        </div>
                                        <div class="col-span-1 flex items-center gap-2">
                                            <template v-if="itemKey === 'color'">
                                                <div class="w-6 h-6 rounded-full" :class="`bg-${item}-600`"></div>
                                            </template>
                                            <template v-else-if="itemKey === 'recaptcha_score'">
                                                <span v-if="item === null" class="text-gray-400">—</span>
                                                <template v-else>
                                                    {{ item }}
                                                    <RecaptchaBadge :score="item" />
                                                </template>
                                            </template>
                                            <template v-else-if="itemKey === 'reminder'">
                                                <Boolean :value="item" />
                                            </template>
                                            <template v-else>
                                                {{ item }}
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div
                                v-if="metaIndex === 0 && props.reservationSlot.reservation?.attribution"
                                class="grid grid-cols-2 text-sm gap-2 py-0.75 hover:bg-gray-100 dark:hover:bg-neutral-800"
                            >
                                <div class="col-span-1 font-bold">Zdroj</div>
                                <div class="col-span-1 flex items-center gap-2">
                                    <AttributionLine :attribution="props.reservationSlot.reservation.attribution" />
                                </div>
                            </div>

                            <div v-if="props.selectColor">
                                <table
                                    class="border border-collapse border-gray-300 dark:border-neutral-900 [&_td]:border [&_td]:border-gray-300 [&_td]:dark:border-neutral-900 w-full [&_th]:border [&_th]:border-gray-300 [&_th]:dark:border-neutral-900 mt-4 text-sm rounded shadow"
                                >
                                    <thead>
                                        <tr>
                                            <th colspan="3" class="px-3 py-2 text-left bg-gray-100 dark:bg-zinc-800">
                                                {{ lang('reservations::reservations.meta.fields.color') }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            v-for="entry in [
                                                { color: 'red', label: 'Dvořák ml.' },
                                                { color: 'purple', label: 'Dvořák st.' },
                                                { color: 'blue', label: 'Firmy' },
                                                { color: 'green', label: 'Zaměstnanci' },
                                                { color: 'yellow', label: 'Výchozí' },
                                            ]"
                                            :key="entry.color"
                                            :class="[
                                                entry.color === color
                                                    ? 'bg-gray-100 dark:bg-zinc-800'
                                                    : 'bg-white dark:bg-neutral-950',
                                            ]"
                                        >
                                            <td class="px-2 py-1 text-center" width="50">
                                                <AdminRadio
                                                    :id="`color-${entry.color}`"
                                                    v-model="color"
                                                    :value="`${entry.color}`"
                                                />
                                            </td>
                                            <td class="px-2 py-1 text-center" width="50">
                                                <label :for="`color-${entry.color}`" class="cursor-pointer">
                                                    <span
                                                        class="block w-6 h-6 mx-auto rounded-full"
                                                        :class="`bg-${entry.color}-600`"
                                                    ></span>
                                                </label>
                                            </td>
                                            <th class="pl-2 text-left">
                                                <label :for="`color-${entry.color}`" class="block cursor-pointer">{{
                                                    entry.label
                                                }}</label>
                                            </th>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <fieldset class="px-4 py-2">
                <div class="flex space-x-8">
                    <div class="flex-auto text-center">
                        <Button color="default" @click="close">
                            {{ lang('general.close') }}
                        </Button>
                    </div>
                </div>
            </fieldset>
        </div>
    </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { dateHumanReadable } from '@src/Common/Helpers/dates';
import { useToast } from '@src/Common/Composables/useToast';
import AdminRadio from '@src/Fields/Pages/Fields/AdminRadio.vue';
import AttributionLine from '@src/Marketing/Pages/Attribution/AttributionLine.vue';
import RecaptchaBadge from '@src/Reservations/Pages/RecaptchaBadge.vue';

interface Props {
    reservationSlot?: any;
    metas: {
        [key: string]: {
            name: string;
            class: string;
        };
    } | null;
    selectColor?: boolean;
    massCreate?: boolean;
    widgets?: Record<string, Array<{ component: string; data: Record<string, any>; priority: number }>>;
}

const props = withDefaults(defineProps<Props>(), {
    reservationSlot: {},
    selectColor: false,
    massCreate: false,
    widgets: () => ({}),
});

const emit = defineEmits(['close', 'deleted', 'updated']);

const close = () => {
    emit('close');
};

const metas = computed(() => {
    if (!props.metas) {
        return [];
    }

    return Object.keys(props.metas).map((key) => ({
        key,
        name: props.metas[key].name,
    }));
});

const deleteReservation = (id) => {
    axios.delete(route('reservations.delete', { id })).then(() => {
        emit('deleted');
        emit('close');
    });
};

/**
 * Slot color
 *
 * TODO: add update for other data
 */
const color = ref(props.reservationSlot?.reservation?.detail[0]?.data?.color ?? 'yellow');

watch(color, (value) => {
    axios
        .post(route('reservations.details.update', { id: props.reservationSlot?.reservation?.detail[0]?.id }), {
            color: value,
        })
        .then(({ data }) => {
            useToast({ message: data.message });

            emit('updated');
        });
});
</script>
